import { useMemo } from 'react';
import ReactSelect from 'react-select';
import type { GroupBase, StylesConfig } from 'react-select';
import { VirtualizedMenuList } from './VirtualizedMenuList';

export interface SelectOption {
    value: string;
    label: string;
}

export interface SelectProps {
    options: SelectOption[];
    placeholder?: string;
    value?: string | string[];
    onChange?: (value: any) => void;
    label?: string;
    error?: string;
    isDisabled?: boolean;
    isMulti?: boolean;
    className?: string;
    required?: boolean;
    styles?: StylesConfig<SelectOption, boolean, GroupBase<SelectOption>>;
    isOptionDisabled?: (option: SelectOption) => boolean;
    /** Virtualize the dropdown list — use for long option lists (100+) to avoid UI lag */
    virtualizeMenuList?: boolean;
    /** Clear control on the control (defaults to shown when there is a value — set false to hide e.g. multi-select) */
    isClearable?: boolean;
}

// ─── Custom styles — matches our design system ──────────────────────────────
const customStyles: StylesConfig<SelectOption, false, GroupBase<SelectOption>> = {
    control: (base, state) => ({
        ...base,
        minHeight: '41px',
        height: 'auto',
        fontSize: '14px',
        borderRadius: '6px',
        borderColor: state.isFocused ? '#017DF2' : '#E6E6E6',
        backgroundColor: state.isDisabled ? '#F3F4F6' : '#FAFCFF',
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        opacity: state.isDisabled ? 1 : 1,
        '&:hover': {
            borderColor: state.isFocused ? '#E6E6E6' : '#E6E6E6',
        },
        '@media (max-width: 1535px)': {
            minHeight: '32px',
            fontSize: '12px',
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '0 12px',
        minHeight: '36px',
        height: 'auto',
        '@media (max-width: 1535px)': {
            minHeight: '32px',
        },
    }),
    multiValue: (base) => ({
        ...base,
        maxWidth: '90%',
    }),
    multiValueLabel: (base) => ({
        ...base,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
    }),
    placeholder: (base) => ({
        ...base,
        color: '#808080',
        fontSize: '14px',
        margin: 0,
        '@media (max-width: 1535px)': {
            fontSize: '12px',
        },
    }),
    singleValue: (base) => ({
        ...base,
        color: '#231F20',
        fontSize: '13px',
        margin: 0,
        '@media (max-width: 1535px)': {
            fontSize: '12px',
        },
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({
        ...base,
        padding: '0 6px 0 0',
        color: '#808080',
        '&:hover': { color: '#231F20' },
        svg: {
            '@media (max-width: 1535px)': {
                width: '14px',
                height: '14px',
            },
        },
    }),
    menu: (base) => ({
        ...base,
        borderRadius: '8px',
        overflow: 'hidden',
        zIndex: 9999,
    }),
    menuList: (base) => ({
        ...base,
        padding: '4px',
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        color: state.isSelected ? '#ffffff' : '#231F20',
        backgroundColor: state.isSelected
            ? '#017DF2'
            : state.isFocused
                ? '#F2F8FE'
                : 'transparent',
        borderRadius: '4px',
        padding: '6px 10px',
        cursor: 'pointer',
        '@media (max-width: 1535px)': {
            fontSize: '11px',
            padding: '5px 10px',
        },
    }),
    clearIndicator: (base) => ({
        ...base,
        padding: '0 6px 0 0',
        color: '#808080',
        svg: {
            '@media (max-width: 1535px)': {
                width: '14px',
                height: '14px',
            },
        },
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

// ─── Select component ────────────────────────────────────────────────────────
const Select = ({
    options,
    placeholder,
    value,
    onChange,
    label,
    error,
    isDisabled = false,
    isMulti = false,
    className = '',
    required,
    styles,
    isOptionDisabled,
    virtualizeMenuList = false,
    isClearable,
}: SelectProps) => {
    const selectStyles = useMemo(() => {
        if (!virtualizeMenuList) {
            return { ...customStyles, ...styles } as StylesConfig<
                SelectOption,
                boolean,
                GroupBase<SelectOption>
            >;
        }
        return {
            ...customStyles,
            ...styles,
            menuList: (base, props) => {
                let merged = base;
                if (customStyles.menuList) {
                    merged = customStyles.menuList(merged, props);
                }
                if (styles?.menuList) {
                    merged = styles.menuList(merged, props);
                }
                return {
                    ...merged,
                    // Keep only the virtual list scrollable (inner), not this wrapper.
                    overflow: 'visible',
                    overflowY: 'visible',
                    maxHeight: 'none',
                };
            },
        } as StylesConfig<SelectOption, boolean, GroupBase<SelectOption>>;
    }, [styles, virtualizeMenuList]);

    let selected: any = null;
    if (isMulti && Array.isArray(value)) {
        // Use Set lookup to avoid O(options * selectedValues) cost for large multi-selects.
        const selectedValues = new Set(value);
        selected = options.filter(o => selectedValues.has(o.value));
    } else if (!isMulti && typeof value === 'string') {
        selected = options.find(o => o.value === value) ?? null;
    }
    const selectedCount = isMulti && Array.isArray(selected) ? selected.length : 0;
    const remainingOptionsCount = isMulti ? Math.max(options.length - selectedCount, 0) : options.length;
    // In multi-select mode, close when user picks the final remaining option.
    const shouldCloseOnSelect = !isMulti || remainingOptionsCount <= 1;
    
    return (
        // i am removing the bottom margin for the select component because it cause design issues at multiple places
        // mb-10px 2xl:mb-15px
        <div className={`w-full ${className}`}>
            {label && (
                <label className="text-[12px] 2xl:text-[14px] font-medium text-[var(--secondary-color)] mb-1">
                    {label}
                    {required && <span className="text-[var(--semantic-error-color)] ml-0.5">*</span>}
                </label>
            )}

            <ReactSelect<SelectOption, typeof isMulti>
                options={options}
                value={selected}
                placeholder={placeholder}
                isMulti={isMulti}
                onChange={val => {
                    if (isMulti) {
                        const vals = (val as SelectOption[]).map(v => v.value);
                        onChange?.(vals);
                    } else {
                        onChange?.((val as SelectOption)?.value ?? '');
                    }
                }}
                styles={selectStyles as any}
                isDisabled={isDisabled}
                isOptionDisabled={isOptionDisabled}
                isClearable={isClearable !== undefined ? isClearable : !!value}
                classNamePrefix="rselect"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                captureMenuScroll={!virtualizeMenuList}
                closeMenuOnSelect={shouldCloseOnSelect}
                components={
                    virtualizeMenuList ? { MenuList: VirtualizedMenuList } : undefined
                }
            />

            {error && (
                <span className="text-[11px] text-[var(--semantic-error-color)] mt-0.5" role="alert">
                    {error}
                </span>
            )}
        </div>
    );
};

export default Select;
